import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

const OUTCOME_META = {
  success:      { label:'✅ Success',      color:'#16a34a', bg:'#dcfce7' },
  partial:      { label:'⚠️ Partial',      color:'#d97706', bg:'#fef3c7' },
  complication: { label:'⚡ Complication', color:'#dc2626', bg:'#fee2e2' },
  failure:      { label:'❌ Failure',      color:'#991b1b', bg:'#fecaca' },
  pending:      { label:'⏳ Pending',      color:'#475569', bg:'#f1f5f9' },
};

const emptyForm = () => ({
  treatmentId:'', patientId:'', treatmentName:'', patientName:'', doctorName:'',
  outcome:'success', painLevel:'', satisfaction:'', complications:'', patientFeedback:'', clinicianNotes:'',
  needsRetreatment:false, retreatmentScheduled:false, refundIssued:false,
});

const TreatmentOutcomesPage = ({ API_BASE }) => {
  const [tab, setTab] = useState('stats');
  const [outcomes, setOutcomes] = useState([]);
  const [stats, setStats] = useState([]);
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'stats') {
        const r = await axios.get(`${API_BASE}/outcomes/stats`);
        setStats(r.data || []);
      } else {
        const url = outcomeFilter ? `/outcomes?outcome=${outcomeFilter}` : '/outcomes';
        const r = await axios.get(`${API_BASE}${url}`);
        setOutcomes(r.data || []);
      }
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [API_BASE, tab, outcomeFilter]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(emptyForm()); setEditingId(null); setShowForm(true); };
  const openEdit = (o) => {
    setForm({
      ...emptyForm(), ...o,
      complications: (o.complications || []).join(', '),
      painLevel: o.painLevel ?? '',
      satisfaction: o.satisfaction ?? '',
    });
    setEditingId(o._id);
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.treatmentId && !editingId) return alert('Treatment ID is required (link to a completed treatment)');
    const body = {
      ...form,
      complications: form.complications ? form.complications.split(',').map(s => s.trim()).filter(Boolean) : [],
      painLevel: form.painLevel === '' ? null : Number(form.painLevel),
      satisfaction: form.satisfaction === '' ? null : Number(form.satisfaction),
    };
    try {
      if (editingId) await axios.put(`${API_BASE}/outcomes/${editingId}`, body);
      else           await axios.post(`${API_BASE}/outcomes`, body);
      setShowForm(false); load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this outcome record?')) return;
    try {
      await axios.delete(`${API_BASE}/outcomes/${id}`);
      load();
    } catch (e) { alert('Delete failed: ' + (e.response?.data?.error || e.message)); }
  };

  const rateColor = (pct) => {
    if (pct >= 90) return { bg:'#dcfce7', fg:'#166534' };
    if (pct >= 75) return { bg:'#e0f2fe', fg:'#075985' };
    if (pct >= 60) return { bg:'#fef3c7', fg:'#92400e' };
    return { bg:'#fee2e2', fg:'#991b1b' };
  };

  return (
    <div style={{ padding:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:10 }}>
        <h2 style={{ color: theme.navy, margin:0 }}>🏆 Treatment Outcome Tracking</h2>
        <button onClick={openAdd} style={{ ...btnStyle, background:theme.primary, color:'white' }}>+ Record Outcome</button>
      </div>

      <div style={{ ...cardStyle, marginBottom:12, background:theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.85rem', color:theme.navy, lineHeight:1.5 }}>
          <b>Why this matters:</b> Track treatment outcomes (success, complications, pain, patient satisfaction) to identify high-failure procedures, retrain doctors, improve protocols — and to market success rates to patients for trust.
        </div>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        {[['stats','📊 Success Rates'], ['list','📋 All Outcomes']].map(([k, lbl]) => (
          <button key={k} onClick={()=>setTab(k)}
            style={{ padding:'8px 18px', border:'none', borderRadius:8, fontWeight:600, fontSize:'0.85rem', cursor:'pointer',
              background: tab===k ? theme.primary : '#f1f3f5', color: tab===k ? 'white' : '#333' }}>
            {lbl}
          </button>
        ))}
      </div>

      {loading && <div style={{ padding:40, textAlign:'center', color:'#888' }}>Loading…</div>}

      {/* STATS TAB */}
      {!loading && tab === 'stats' && (
        <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
          {stats.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No outcomes recorded yet. Add one to see stats.</div> :
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#f9fafb' }}>
              <th style={th}>Treatment</th>
              <th style={th}>Total</th>
              <th style={th}>Success Rate</th>
              <th style={th}>Complication Rate</th>
              <th style={th}>Breakdown</th>
              <th style={th}>Avg Pain</th>
              <th style={th}>Avg Satisfaction</th>
            </tr></thead>
            <tbody>
              {stats.map((s, i) => {
                const rc = rateColor(s.successRate);
                const cc = rateColor(100 - s.complicationRate);
                return (
                  <tr key={i} style={{ borderBottom:'1px solid #f1f3f5' }}>
                    <td style={{ ...td, fontWeight:600 }}>{s.treatmentName}</td>
                    <td style={td}>{s.total}</td>
                    <td style={td}>
                      <span style={{ padding:'3px 12px', borderRadius:12, fontSize:'0.8rem', fontWeight:700, background:rc.bg, color:rc.fg }}>
                        {s.successRate}%
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ padding:'3px 12px', borderRadius:12, fontSize:'0.8rem', fontWeight:700, background:cc.bg, color:cc.fg }}>
                        {s.complicationRate}%
                      </span>
                    </td>
                    <td style={{ ...td, fontSize:'0.75rem' }}>
                      <span style={{ color:'#166534' }}>✓ {s.breakdown.success}</span> ·
                      <span style={{ color:'#d97706', marginLeft:6 }}>⚠ {s.breakdown.partial}</span> ·
                      <span style={{ color:'#dc2626', marginLeft:6 }}>⚡ {s.breakdown.complication}</span> ·
                      <span style={{ color:'#991b1b', marginLeft:6 }}>✗ {s.breakdown.failure}</span>
                    </td>
                    <td style={td}>{s.avgPain != null ? `${s.avgPain}/10` : '—'}</td>
                    <td style={td}>{s.avgSatisfaction != null ? `${s.avgSatisfaction}/5 ⭐` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>}
        </div>
      )}

      {/* LIST TAB */}
      {!loading && tab === 'list' && (
        <div>
          <div style={{ display:'flex', gap:8, marginBottom:12 }}>
            {['', ...Object.keys(OUTCOME_META)].map(k => (
              <button key={k||'all'} onClick={()=>setOutcomeFilter(k)}
                style={{ padding:'5px 12px', fontSize:'0.78rem', borderRadius:6, border:'1px solid #e5e7eb', cursor:'pointer',
                  background: outcomeFilter===k ? theme.primary : 'white', color: outcomeFilter===k ? 'white' : '#333', fontWeight:600 }}>
                {k ? OUTCOME_META[k].label : 'All'}
              </button>
            ))}
          </div>

          <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
            {outcomes.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No outcomes found.</div> :
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#f9fafb' }}>
                <th style={th}>Patient</th><th style={th}>Treatment</th>
                <th style={th}>Doctor</th><th style={th}>Outcome</th>
                <th style={th}>Pain</th><th style={th}>Satisfaction</th>
                <th style={th}>Days After</th><th style={th}>Follow-up</th>
                <th style={th}>Flags</th><th style={th}>Actions</th>
              </tr></thead>
              <tbody>
                {outcomes.map(o => {
                  const meta = OUTCOME_META[o.outcome] || OUTCOME_META.pending;
                  return (
                    <tr key={o._id} style={{ borderBottom:'1px solid #f1f3f5' }}>
                      <td style={{ ...td, fontWeight:600 }}>{o.patientName || '—'}</td>
                      <td style={td}>{o.treatmentName || '—'}</td>
                      <td style={td}>{o.doctorName || '—'}</td>
                      <td style={td}>
                        <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.72rem', fontWeight:700, background:meta.bg, color:meta.color }}>
                          {meta.label}
                        </span>
                      </td>
                      <td style={td}>{o.painLevel != null ? `${o.painLevel}/10` : '—'}</td>
                      <td style={td}>{o.satisfaction != null ? `${o.satisfaction}/5` : '—'}</td>
                      <td style={td}>{o.daysAfterTreatment != null ? `${o.daysAfterTreatment}d` : '—'}</td>
                      <td style={{ ...td, fontSize:'0.78rem', color:'#666' }}>{o.followUpDate ? new Date(o.followUpDate).toLocaleDateString('en-IN') : '—'}</td>
                      <td style={{ ...td, fontSize:'0.72rem' }}>
                        {o.needsRetreatment && <span style={{ color:theme.danger, fontWeight:600 }}>🔁 Retreat</span>}
                        {o.refundIssued && <span style={{ color:theme.warning, fontWeight:600, marginLeft:6 }}>💰 Refund</span>}
                        {o.complications?.length > 0 && <div style={{ color:'#666' }}>{o.complications.join(', ')}</div>}
                      </td>
                      <td style={td}>
                        <button onClick={()=>openEdit(o)} style={tinyBtn}>Edit</button>
                        <button onClick={()=>remove(o._id)} style={{ ...tinyBtn, background:theme.danger, color:'white', border:'none' }}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>}
          </div>
        </div>
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div style={modalBg} onClick={()=>setShowForm(false)}>
          <div style={modalCard} onClick={e=>e.stopPropagation()}>
            <h3 style={{ margin:'0 0 14px', color:theme.navy }}>{editingId ? 'Edit' : 'Record'} Treatment Outcome</h3>
            {!editingId && (
              <>
                <Label>Treatment ID (from Treatments page)</Label>
                <input value={form.treatmentId} onChange={e=>setForm({...form, treatmentId:e.target.value})} style={inputStyle} placeholder="Mongo ObjectId of the Treatment document"/>
              </>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:10 }}>
              <div>
                <Label>Treatment Name</Label>
                <input value={form.treatmentName} onChange={e=>setForm({...form, treatmentName:e.target.value})} style={inputStyle}/>
              </div>
              <div>
                <Label>Patient Name</Label>
                <input value={form.patientName} onChange={e=>setForm({...form, patientName:e.target.value})} style={inputStyle}/>
              </div>
              <div>
                <Label>Doctor</Label>
                <input value={form.doctorName} onChange={e=>setForm({...form, doctorName:e.target.value})} style={inputStyle}/>
              </div>
              <div>
                <Label>Outcome</Label>
                <select value={form.outcome} onChange={e=>setForm({...form, outcome:e.target.value})} style={inputStyle}>
                  {Object.entries(OUTCOME_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Pain Level (0-10)</Label>
                <input type="number" min={0} max={10} value={form.painLevel} onChange={e=>setForm({...form, painLevel:e.target.value})} style={inputStyle}/>
              </div>
              <div>
                <Label>Satisfaction (1-5)</Label>
                <input type="number" min={1} max={5} value={form.satisfaction} onChange={e=>setForm({...form, satisfaction:e.target.value})} style={inputStyle}/>
              </div>
            </div>
            <div style={{ marginTop:10 }}>
              <Label>Complications (comma separated)</Label>
              <input value={form.complications} onChange={e=>setForm({...form, complications:e.target.value})} style={inputStyle} placeholder="swelling, pain, infection"/>
            </div>
            <div style={{ marginTop:10 }}>
              <Label>Patient Feedback</Label>
              <textarea value={form.patientFeedback} onChange={e=>setForm({...form, patientFeedback:e.target.value})} style={{ ...inputStyle, minHeight:60 }}/>
            </div>
            <div style={{ marginTop:10 }}>
              <Label>Clinician Notes</Label>
              <textarea value={form.clinicianNotes} onChange={e=>setForm({...form, clinicianNotes:e.target.value})} style={{ ...inputStyle, minHeight:60 }}/>
            </div>
            <div style={{ marginTop:10, display:'flex', gap:16, flexWrap:'wrap' }}>
              <label style={{ fontSize:'0.85rem', display:'flex', gap:6, alignItems:'center' }}>
                <input type="checkbox" checked={form.needsRetreatment} onChange={e=>setForm({...form, needsRetreatment:e.target.checked})}/>
                Needs retreatment
              </label>
              <label style={{ fontSize:'0.85rem', display:'flex', gap:6, alignItems:'center' }}>
                <input type="checkbox" checked={form.retreatmentScheduled} onChange={e=>setForm({...form, retreatmentScheduled:e.target.checked})}/>
                Retreatment scheduled
              </label>
              <label style={{ fontSize:'0.85rem', display:'flex', gap:6, alignItems:'center' }}>
                <input type="checkbox" checked={form.refundIssued} onChange={e=>setForm({...form, refundIssued:e.target.checked})}/>
                Refund issued
              </label>
            </div>

            <div style={{ marginTop:16, display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={()=>setShowForm(false)} style={{ ...btnStyle, background:'#e5e7eb', color:'#333' }}>Cancel</button>
              <button onClick={submit} style={{ ...btnStyle, background:theme.primary, color:'white' }}>{editingId ? 'Update' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Label = ({ children }) => <div style={{ fontSize:'0.75rem', color:theme.textMuted, fontWeight:600, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' }}>{children}</div>;
const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };
const tinyBtn = { padding:'4px 10px', fontSize:'0.75rem', border:'1px solid #e5e7eb', background:'white', borderRadius:6, cursor:'pointer', marginRight:4 };
const modalBg = { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 };
const modalCard = { background:'white', borderRadius:14, padding:24, maxWidth:680, width:'100%', maxHeight:'90vh', overflowY:'auto' };

export default TreatmentOutcomesPage;
